import {
  AtimInterface,
  AutonomousActivityType,
  autonomousActivityTypeToJSON,
  DriveType,
  HitchType,
  HydraulicCouplerType,
  PtoSpeed,
  PtoType,
  ptoTypeToJSON,
} from "protos/atk/enums";
import { CarrierSolution } from "protos/rtc/controls_messages/gps";
import { Direction } from "protos/rtc/path_plan";
import {
  GuidePathStatus,
  Objective_ObjectiveType as ObjectiveType,
  State,
} from "protos/rtc/jobs";
import { JobType } from "protos/atk/job_type";
import { OverrideReason } from "protos/rtc/controls_messages/overwatch";
import { PivotAlertUnknownReason } from "protos/rtc/web_socket";
import { SafetyState } from "protos/overwatch/overwatch";
import { TFunction } from "i18next";

export const hitchTypeToLocaleString = (h: HitchType): string => {
  switch (h) {
    case HitchType.HITCH_TYPE_DRAW_BAR_REAR: {
      return "models.equipment.hitchTypes.drawbarRear";
    }
    case HitchType.HITCH_TYPE_THREE_POINT_FRONT: {
      return "models.equipment.hitchTypes.threePointFront";
    }
    case HitchType.HITCH_TYPE_THREE_POINT_REAR: {
      return "models.equipment.hitchTypes.threePointRear";
    }
    case HitchType.HITCH_TYPE_UNSPECIFIED: {
      return "utils.descriptors.unknown";
    }
    case HitchType.UNRECOGNIZED: {
      return "utils.descriptors.unknown";
    }
  }
};

export const driveTypeToLocaleString = (d: DriveType): string => {
  switch (d) {
    case DriveType.DRIVE_TYPE_FRONT_AXLE_STEER: {
      return "models.equipment.driveTypes.frontAxleSteer";
    }
    case DriveType.DRIVE_TYPE_DIFFERENTIAL_STEER: {
      return "models.equipment.driveTypes.differentialSteer";
    }
    case DriveType.DRIVE_TYPE_UNSPECIFIED: {
      return "utils.descriptors.unknown";
    }
    case DriveType.UNRECOGNIZED: {
      return "utils.descriptors.unknown";
    }
  }
};

export const ptoTypeToLocationString = (p: PtoType): string => {
  switch (p) {
    case PtoType.PTO_TYPE_FRONT: {
      return "models.equipment.ptoTypes.front";
    }
    case PtoType.PTO_TYPE_FRONT_AND_REAR: {
      return "models.equipment.ptoTypes.frontAndRear";
    }
    case PtoType.PTO_TYPE_REAR: {
      return "models.equipment.ptoTypes.rear";
    }
    case PtoType.PTO_TYPE_NONE: {
      return "models.equipment.ptoTypes.none";
    }
    case PtoType.PTO_TYPE_UNSPECIFIED: {
      return "utils.descriptors.unknown";
    }
    case PtoType.UNRECOGNIZED: {
      return "utils.descriptors.unknown";
    }
  }
};

export const ptoTypeToNameString = (t: TFunction, ptoType: PtoType): string => {
  switch (ptoType) {
    case PtoType.PTO_TYPE_FRONT: {
      return t("models.equipment.ptoNames.front");
    }
    case PtoType.PTO_TYPE_REAR: {
      return t("models.equipment.ptoNames.rear");
    }
    default: {
      return ptoTypeToJSON(ptoType);
    }
  }
};

// These are standardized values and loosely track to RPMs. For example 540E equates to roughly 700 rpm.
export const ptoSpeedToLocaleString = (p: PtoSpeed): string => {
  switch (p) {
    case PtoSpeed.PTO_SPEED_540: {
      return "540";
    }
    case PtoSpeed.PTO_SPEED_540E: {
      return "540E";
    }
    case PtoSpeed.PTO_SPEED_1000: {
      return "1000";
    }
    case PtoSpeed.PTO_SPEED_1000E: {
      return "1000E";
    }
    case PtoSpeed.UNRECOGNIZED: {
      return "utils.descriptors.unknown";
    }
    case PtoSpeed.PTO_SPEED_UNSPECIFIED: {
      return "utils.descriptors.unknown";
    }
  }
};

export const atimInterfaceToLocaleString = (a: AtimInterface): string => {
  switch (a) {
    case AtimInterface.ATIM_GENERIC: {
      return "models.equipment.atimInterfaces.generic";
    }
    case AtimInterface.ATIM_LASER_WEEDER: {
      return "models.equipment.atimInterfaces.laserWeeder";
    }
    case AtimInterface.ATIM_NONE: {
      return "utils.descriptors.none";
    }
    case AtimInterface.UNRECOGNIZED: {
      return "utils.descriptors.unknown";
    }
  }
};

export const couplerTypeToLocaleString = (
  t: TFunction,
  ct: HydraulicCouplerType
): string => {
  switch (ct) {
    case HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5: {
      return t("models.equipment.hydraulicCouplers.iso5675Size12pt5");
    }
    case HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20: {
      return t("models.equipment.hydraulicCouplers.iso5675Size20");
    }
    default: {
      return t("utils.descriptors.unknown");
    }
  }
};

export const jobTypeToLocaleString = (t: TFunction, jt: JobType): string => {
  switch (jt) {
    case JobType.LASER_WEED: {
      return t("models.autotractor.jobTypes.laserWeed");
    }
    case JobType.GROUND_PREP: {
      return t("models.autotractor.jobTypes.groundPrep");
    }
    case JobType.TRANSPORT: {
      return t("models.autotractor.jobTypes.transport");
    }
    case JobType.IN_SEASON: {
      return t("models.autotractor.jobTypes.inSeason");
    }
    case JobType.UNRECOGNIZED:
    case JobType.JOB_TYPE_UNSPECIFIED: {
      return t("models.autotractor.jobTypes.unrecognized", { value: jt });
    }
    default: {
      return t("models.autotractor.jobTypes.unrecognized", { value: jt });
    }
  }
};

export function objectiveTypeToLocaleString(
  t: TFunction,
  ot: ObjectiveType
): string {
  switch (ot) {
    case ObjectiveType.LASER_WEED_ROW: {
      return t("models.autotractor.objectiveTypes.laserWeedRow");
    }
    case ObjectiveType.GROUND_PREP_SECTION: {
      return t("models.autotractor.objectiveTypes.groundPrepSection");
    }
    case ObjectiveType.TRANSPORT: {
      return t("models.autotractor.objectiveTypes.transport");
    }
    case ObjectiveType.IN_SEASON_ROW: {
      return t("models.autotractor.objectiveTypes.inSeasonRow");
    }
    default: {
      return t("utils.descriptors.unknown");
    }
  }
}

export function guidePathStatusToLocaleString(
  t: TFunction,
  status: GuidePathStatus
): string {
  switch (status) {
    case GuidePathStatus.GUIDE_PATH_STATUS_ERROR_EQUIPMENT: {
      return t("views.autotractor.jobDetails.transitions.error_equipment");
    }
    case GuidePathStatus.GUIDE_PATH_STATUS_ERROR_INTERNAL: {
      return t("views.autotractor.jobDetails.transitions.error_internal");
    }
    case GuidePathStatus.GUIDE_PATH_STATUS_ERROR_PATH_PLANNING: {
      return t("views.autotractor.jobDetails.transitions.error_path_planning");
    }
    default: {
      return t("utils.descriptors.unknown");
    }
  }
}

export function directionToLocaleString(
  t: TFunction,
  direction: Direction
): string {
  switch (direction) {
    case Direction.CW: {
      return t("utils.units.cwLong");
    }
    case Direction.CCW: {
      return t("utils.units.ccwLong");
    }
    default: {
      return t("utils.descriptors.unknown");
    }
  }
}

export function stateToLocaleString(t: TFunction, state: State): string {
  switch (state) {
    case State.ACKNOWLEDGED: {
      return t("models.autotractor.states.acknowledged");
    }
    case State.CANCELLED: {
      return t("models.autotractor.states.cancelled");
    }
    case State.COMPLETED: {
      return t("models.autotractor.states.completed");
    }
    case State.FAILED: {
      return t("models.autotractor.states.failed");
    }
    case State.IN_PROGRESS: {
      return t("models.autotractor.states.inProgress");
    }
    case State.NEW: {
      return t("models.autotractor.states.new");
    }
    case State.PAUSED: {
      return t("models.autotractor.states.paused");
    }
    case State.PENDING: {
      return t("models.autotractor.states.pending");
    }
    case State.READY: {
      return t("models.autotractor.states.ready");
    }
    case State.UNRECOGNIZED: {
      return t("models.autotractor.states.unrecognized", { value: state });
    }
    default: {
      return t("utils.descriptors.unknown");
    }
  }
}

export function gpsSolutionToString(
  t: TFunction,
  soln: CarrierSolution
): string {
  switch (soln) {
    case CarrierSolution.FIXED_SOLUTION: {
      return t("views.rtc.tooltips.gpsSolution.fixed");
    }
    case CarrierSolution.FLOATING_SOLUTION: {
      return t("views.rtc.tooltips.gpsSolution.floating");
    }
    case CarrierSolution.NO_SOLUTION: {
      return t("views.rtc.tooltips.gpsSolution.noGps");
    }
    default: {
      return t("views.rtc.tooltips.gpsSolution.unknown");
    }
  }
}

export function overrideReasonToLocaleString(
  t: TFunction,
  reason: OverrideReason
): string {
  switch (reason) {
    case OverrideReason.OVERRIDE_REASON_FALSE_TRIP: {
      return t("views.rtc.overwatch.reasons.falseTrip");
    }
    case OverrideReason.OVERRIDE_REASON_DIVERT: {
      return t("views.rtc.overwatch.reasons.divert");
    }
    case OverrideReason.OVERRIDE_REASON_NARROW_PASSAGE: {
      return t("views.rtc.overwatch.reasons.narrowPassage");
    }
    case OverrideReason.OVERRIDE_REASON_ROADING: {
      return t("views.rtc.overwatch.reasons.roading");
    }
    case OverrideReason.OVERRIDE_REASON_OTHER: {
      return t("views.rtc.overwatch.reasons.other");
    }
    default: {
      return t("utils.descriptors.unknown");
    }
  }
}

export function safetyStateToLocaleString(
  t: TFunction,
  state: SafetyState
): string {
  switch (state) {
    case SafetyState.SAFETY_STATE_NOMINAL: {
      return t("views.rtc.overwatch.safetyState.nominal");
    }
    case SafetyState.SAFETY_STATE_SPEED_LIMITED: {
      return t("views.rtc.overwatch.safetyState.speedLimited");
    }
    case SafetyState.SAFETY_STATE_STOPPED: {
      return t("views.rtc.overwatch.safetyState.stopped");
    }
    case SafetyState.SAFETY_STATE_SAFETY_HOLD: {
      return t("views.rtc.overwatch.safetyState.safetyHold");
    }
    default: {
      return t("views.rtc.overwatch.safetyState.unknown");
    }
  }
}

export function pivotAlertUnknownReasonToString(
  t: TFunction,
  reason: PivotAlertUnknownReason
): string {
  switch (reason) {
    case PivotAlertUnknownReason.REASON_FARM_DATA_STALE: {
      return t("models.autotractor.pivotAlertUnknownReasons.farmDataStale");
    }
    case PivotAlertUnknownReason.REASON_INTERNAL_ERROR: {
      return t("models.autotractor.pivotAlertUnknownReasons.internalError");
    }
    case PivotAlertUnknownReason.REASON_OUTSIDE_ALL_TRACKED_PIVOTS: {
      return t(
        "models.autotractor.pivotAlertUnknownReasons.outsideAllTrackedPivots"
      );
    }
    case PivotAlertUnknownReason.REASON_PIVOT_LOCATION_STALE: {
      return t(
        "models.autotractor.pivotAlertUnknownReasons.pivotLocationStale"
      );
    }
    default: {
      return t("models.autotractor.pivotAlertUnknownReasons.unknownReason", {
        reason,
      });
    }
  }
}

export function autonomousActivityToString(
  t: TFunction,
  autonomousActivity: AutonomousActivityType
): string {
  switch (autonomousActivity) {
    case AutonomousActivityType.ACTIVITY_PAUSE: {
      return t("views.autotractor.autonomousActivities.activities.pause");
    }
    case AutonomousActivityType.ACTIVITY_SHUTDOWN: {
      return t("views.autotractor.autonomousActivities.activities.shutdown");
    }
    case AutonomousActivityType.ACTIVITY_STARTUP: {
      return t("views.autotractor.autonomousActivities.activities.startup");
    }
    case AutonomousActivityType.ACTIVITY_TRANSPORT: {
      return t("views.autotractor.autonomousActivities.activities.transport");
    }
    case AutonomousActivityType.ACTIVITY_TRAVERSE: {
      return t("views.autotractor.autonomousActivities.activities.eortas");
    }
    case AutonomousActivityType.ACTIVITY_WORKING_DIVERT: {
      return t(
        "views.autotractor.autonomousActivities.activities.workingDiverts"
      );
    }
    case AutonomousActivityType.ACTIVITY_WORKING_HEADLAND: {
      return t(
        "views.autotractor.autonomousActivities.activities.workingHeadlands"
      );
    }
    case AutonomousActivityType.ACTIVITY_WORKING_ROW: {
      return t("views.autotractor.autonomousActivities.activities.workingRows");
    }
    case AutonomousActivityType.ACTIVITY_COVERING_TRACKS: {
      return t(
        "views.autotractor.autonomousActivities.activities.coveringTracks"
      );
    }
    default: {
      // return the symbolic name of the autonomous activity; it's probably more
      // readable than a number and either way getting here is a bug
      return autonomousActivityTypeToJSON(autonomousActivity);
    }
  }
}
